import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, ChevronLeft, ChevronRight, FilePlus2, CopyPlus, ExternalLink } from 'lucide-react';
import WorkerItem, { Worker } from './WorkerItem';
import WorkflowItem, { Workflow } from './WorkflowItem';
import { Sidebar, SidebarHeader, SidebarContent, SearchInput } from '@tadagram/ui';
import { WorkflowAPI } from '../../pages/WorkflowBuilder/api';
import { apiService } from '../../services/api';
import { ContextMenu } from '../../pages/WorkflowBuilder/ContextMenu';

/**
 * WorkerListSidebar - Middle sidebar showing list of workers
 * Design based on Telegram's Sidebar pattern
 * 
 * Features:
 * - Search bar
 * - Scrollable worker list
 * - Footer with worker stats
 * - Active worker highlighting
 * - Collapsible sidebar with smooth animation
 */

interface WorkerListSidebarProps {
  workers: Worker[];
  activeWorkerId?: string;
  onWorkerSelect: (workerId: string) => void;
}

export const WorkerListSidebar: React.FC<WorkerListSidebarProps> = ({
  workers,
  activeWorkerId,
  onWorkerSelect
}) => {
  const { t } = useTranslation('workflow');
  const [searchQuery, setSearchQuery] = useState('');
  const [workflowMode, setWorkflowMode] = useState(false);
  const [workflowItems, setWorkflowItems] = useState<Workflow[]>([]);
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeWorkflowId, setActiveWorkflowId] = useState<string | null>(null);
  const [sidebarWidth, setSidebarWidth] = useState(280);
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; wf: Workflow } | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const MIN_WIDTH = 220;
  const MAX_WIDTH = 500;

  const location = useLocation();

  const navigate = useNavigate();

  const handleDuplicate = useCallback(async (wf: Workflow) => {
    setCtxMenu(null);
    setDuplicatingId(wf.id);
    try {
      const newName = `${wf.name} ${t('copySuffix')}`;
      const result: any = await WorkflowAPI.duplicate(wf.id, newName);
      window.dispatchEvent(new Event('workflow-status-changed'));
      const newId = result?._id || result?.id;
      if (newId) {
        setActiveWorkflowId(newId);
        navigate(`/workflows/${newId}`);
      }
    } catch (e) {
      console.error('Duplicate workflow failed', e);
    } finally {
      setDuplicatingId(null);
    }
  }, [navigate, t]);

  // Function to fetch workflows (extracted for reuse)
  const fetchWorkflows = async () => {
    try {
      const resp: any = await WorkflowAPI.getAll();
      const workflows = resp.workflows || [];

      // Build Workflow[] with explicit fields
      const items: Workflow[] = workflows.map((w: any) => {
        // Calculate account count from account_selector nodes
        let accountCount = 0;
        if (w.nodes && Array.isArray(w.nodes)) {
          w.nodes.forEach((node: any) => {
            if (node.type === 'account_selector') {
              // Check typed config first
              const selectorConfig = node.data?.account_selector_config;
              if (selectorConfig && Array.isArray(selectorConfig.selected_accounts)) {
                accountCount += selectorConfig.selected_accounts.length;
              }
            }
          });
        }

        return {
          id: w._id || w.id,
          name: w.name || 'Unnamed Workflow',
          description: w.description || '',
          nodeCount: (w.nodes && w.nodes.length) || w.node_count || 0,
          edgeCount: (w.edges && w.edges.length) || w.edge_count || 0,
          assignedAccounts: accountCount, // Use calculated count from nodes
          campaignId: w.campaign_id || w.campaignId || null,
          updatedAt: w.updated_at || w.created_at || undefined,
          isActive: w.status === 'active', // Check status from backend
        };
      });

      setWorkflowItems(items);

      // For workflows linked to campaigns, attempt to fetch campaign details
      // to obtain the authoritative assigned account count. This is optional
      // and done per-item to avoid changing backend schema.
      items.forEach(async (it, idx) => {
        if (it.campaignId) {
          try {
            const campaignResp: any = await apiService.get(`/api/v1/campaigns/${it.campaignId}`);
            const assigned = (campaignResp?.campaign?.assigned_accounts || []).length || 0;
            // update state only if different
            setWorkflowItems((prev) => {
              const copy = [...prev];
              const pos = copy.findIndex((x) => x.id === it.id);
              if (pos >= 0 && copy[pos].assignedAccounts !== assigned) {
                copy[pos] = { ...copy[pos], assignedAccounts: assigned } as any;
              }
              return copy;
            });
          } catch (e) {
            // ignore campaign lookup failures
          }
        }
      });
    } catch (err) {
      console.error('Failed to fetch workflows for sidebar:', err);
    }
  };

  useEffect(() => {
    const path = location && location.pathname ? location.pathname : (window.location && window.location.pathname ? window.location.pathname : '');
    if (path.startsWith('/workflows')) {
      setWorkflowMode(true);
      fetchWorkflows();

      // Sync active workflow from URL (e.g. /workflows/:id)
      const match = path.match(/^\/workflows\/([^/]+)/);
      setActiveWorkflowId(match ? match[1] : null);
    } else {
      setWorkflowMode(false);
      setActiveWorkflowId(null);
    }
  }, [location.pathname]);

  // Listen for workflow status changes to refresh the list
  useEffect(() => {
    const handleWorkflowStatusChange = () => {
      if (workflowMode) {
        fetchWorkflows();
      }
    };

    window.addEventListener('workflow-status-changed', handleWorkflowStatusChange);

    return () => {
      window.removeEventListener('workflow-status-changed', handleWorkflowStatusChange);
    };
  }, [workflowMode]);

  // Filter items by search query
  const sourceList = workflowMode ? workflowItems : workers;
  const filteredWorkers = sourceList.filter((worker) =>
    // support both Worker and Workflow shapes
    ((worker as any).workerName && (worker as any).workerName.toLowerCase().includes(searchQuery.toLowerCase())) ||
    ((worker as any).name && (worker as any).name.toLowerCase().includes(searchQuery.toLowerCase())) ||
    ((worker as any).uuid && (worker as any).uuid.toLowerCase().includes(searchQuery.toLowerCase())) ||
    ((worker as any).id && (worker as any).id.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Resize handlers
  const sidebarContainerRef = useRef<HTMLDivElement>(null);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);
  const didDrag = useRef(false);

  const handleTogglePointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return;
    dragStartX.current = e.clientX;
    dragStartWidth.current = sidebarWidth;
    didDrag.current = false;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [sidebarWidth]);

  const handleTogglePointerMove = useCallback((e: React.PointerEvent) => {
    if (!e.buttons) return;
    const dx = e.clientX - dragStartX.current;
    if (!didDrag.current && Math.abs(dx) < 5) return; // threshold to distinguish click vs drag
    didDrag.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const containerLeft = sidebarContainerRef.current?.getBoundingClientRect().left ?? 0;
    const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, e.clientX - containerLeft));
    setSidebarWidth(newWidth);
  }, []);

  const handleTogglePointerUp = useCallback(() => {
    if (didDrag.current) {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    } else {
      setIsExpanded((v) => !v);
    }
    didDrag.current = false;
  }, []);

  return (
    <div ref={sidebarContainerRef} className="flex gap-0 bg-white h-full">
      {/* Sidebar - Smooth collapse/expand animation */}
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden border-r border-gray-200 relative ${isExpanded ? '' : 'w-0'}`}
        style={isExpanded ? { width: `${sidebarWidth}px` } : undefined}
      >
        <Sidebar width={sidebarWidth} className="flex-shrink-0 h-full bg-white">
          {/* Header with Search */}
          <SidebarHeader>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <SearchInput
                  value={searchQuery}
                  onChange={setSearchQuery}
                  onClear={handleClearSearch}
                  placeholder={workflowMode ? 'Search workflows...' : 'Search workers...'}
                  icon={<Search size={18} />}
                />
              </div>
            </div>
          </SidebarHeader>

          {/* Worker List */}
          <SidebarContent>
            {/* Draft workflow item */}
            {workflowMode && !activeWorkflowId && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: '#eff6ff', borderBottom: '1px solid #dbeafe' }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FilePlus2 size={18} color="#3b82f6" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 14, fontWeight: 500, color: '#1d4ed8', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Untitled Workflow</p>
                  <p style={{ fontSize: 12, color: '#93b4f8', margin: 0 }}>Draft · Not saved</p>
                </div>
              </div>
            )}
            {filteredWorkers.length === 0 && searchQuery && (
              <p style={{ textAlign: 'center', color: '#6b7280', padding: '16px', fontSize: 14 }}>No workers found</p>
            )}
            {filteredWorkers.length === 0 && !searchQuery && (
              <p style={{ textAlign: 'center', color: '#6b7280', padding: '16px', fontSize: 14 }}>No workers available</p>
            )}
            {filteredWorkers.map((item: any) => (
              workflowMode ? (
                <div
                  key={item.id}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setCtxMenu({ x: e.clientX, y: e.clientY, wf: item as Workflow });
                  }}
                  className="relative"
                >
                  <WorkflowItem
                    workflow={item}
                    active={item.id === activeWorkflowId}
                    onClick={() => {
                      setActiveWorkflowId(item.id);
                      navigate(`/workflows/${item.id}`);
                    }}
                  />
                  {duplicatingId === item.id && (
                    <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
              ) : (
                <WorkerItem
                  key={item.uuid}
                  worker={item}
                  active={item.uuid === activeWorkerId}
                  onClick={() => onWorkerSelect(item.uuid)}
                />
              )
            ))}
          </SidebarContent>
        </Sidebar>
      </div>

      {/* Context menu for right-click on workflow items */}
      {ctxMenu && (
        <ContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          onClose={() => setCtxMenu(null)}
          items={[
            { type: 'header', label: ctxMenu.wf.name },
            {
              label: t('openWorkflow'),
              icon: <ExternalLink className="w-4 h-4" />,
              onClick: () => {
                setCtxMenu(null);
                setActiveWorkflowId(ctxMenu.wf.id);
                navigate(`/workflows/${ctxMenu.wf.id}`);
              },
            },
            { type: 'separator' },
            {
              label: t('duplicate'),
              icon: <CopyPlus className="w-4 h-4" />,
              disabled: duplicatingId === ctxMenu.wf.id,
              onClick: () => handleDuplicate(ctxMenu.wf),
            },
          ]}
        />
      )}

      {/* Toggle Button — click to collapse, drag to resize.
          `self-center` shrinks this wrapper to the button height so the (-ml-5) overlap
          doesn't span full-height and block the sidebar's scrollbar. */}
      <div className="relative flex items-center justify-center -ml-5 z-10 self-center">
        <button
          onPointerDown={handleTogglePointerDown}
          onPointerMove={handleTogglePointerMove}
          onPointerUp={handleTogglePointerUp}
          className="p-2 rounded-[50%] bg-white border border-gray-200 hover:border-gray-400 hover:bg-gray-100 transition-all duration-300 ease-in-out hover:scale-110 shadow-md hover:shadow-lg flex-shrink-0 cursor-col-resize touch-none"
          title={isExpanded ? 'Collapse / Drag to resize' : 'Expand'}
        >
          {isExpanded ? (
            <ChevronLeft size={20} className="text-gray-600" />
          ) : (
            <ChevronRight size={20} className="text-gray-600" />
          )}
        </button>
      </div>
    </div>
  );
};

export default WorkerListSidebar;
